/**
 * Administration of Non-Medication Component
 * 
 * Displays a list of non-medication administration forms for parents
 * Features:
 * - Shows all submitted non-medication administration forms (Lotion, Sunscreen, etc.)
 * - Allows adding new non-medication administration requests
 * - View logs for each form
 * - Edit existing forms (if allowed)
 * - Type = 0 (Non-Medication)
 */

import React, { useState, useEffect } from 'react'
import { Row, Col, Container, Button, Badge } from 'react-bootstrap'
import Card from '../../../../components/Card'
import { useNavigate, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { 
  fetchMedicationList,
  deleteMedication, 
  selectNonMedicationList, 
  selectListLoading, 
  selectListError, 
  selectNonMedicationMeta,
  clearErrors 
} from '../../../../store/medication/medicationSlice';

import LoaderModal from '../../../../components/loaderModal';
import NoDataFound from '../../../../components/noDataFound';
import { formatDisplayDate } from '../../../../utilities/displayFormat';

//Sweet alert
import Swal from 'sweetalert2'

const NonMedicationAdministrationList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const nonMedicationList = useSelector(selectNonMedicationList);
  const loading = useSelector(selectListLoading);
  const error = useSelector(selectListError);
  const meta = useSelector(selectNonMedicationMeta);

  const [page, setPage] = useState(1);
  const [isFirst, setIsFirst] = useState(true);
  
  // Non-medication types
  const nonMedicationTypes = [
    { label: 'Lotion', icon: 'ri-drop-line' },
    { label: 'Sunscreen', icon: 'ri-sun-line' },
    { label: 'Insect repellent', icon: 'ri-bug-line' },
    { label: 'Lip balm', icon: 'ri-heart-pulse-line' },
    { label: 'Diaper rash cream', icon: 'ri-first-aid-kit-line' }
  ];
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setIsFirst(false)
  }, [isFirst]);

  // Load non-medication list on component mount
  useEffect(() => {
    if (localStorage.getItem("uzoneAuthToken")) {
      dispatch(fetchMedicationList({ type: 0, page: 1, limit: 10 }));
    } else {
      navigate('/')
    }
  }, [dispatch, navigate]);

  // Handle API errors
  useEffect(() => {
    if (error) {
      Swal.fire({
        title: 'Error',
        text: error,
        icon: 'error',
        confirmButtonText: 'Okay'
      }).then(() => {
        dispatch(clearErrors());
      });
    }
  }, [error, dispatch]);

  // Load more data
  const loadMore = () => {
    if (page < meta.last_page) {
      const nextPage = page + 1;
      dispatch(fetchMedicationList({ type: 0, page: nextPage, limit: 10 }));
      setPage(nextPage);
    }
  };

  // Format date helper function
  const formatDate = formatDisplayDate;

  // Format time helper function
  const formatTime = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours);
    const min = parseInt(minutes);
    
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const formattedMinutes = min.toString().padStart(2, '0');
    
    return `${hour12}:${formattedMinutes} ${ampm}`;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return <Badge bg="success">Active</Badge>;
      case 0:
        return <Badge bg="secondary">Inactive</Badge>;
      default:
        return <Badge bg="warning">Pending</Badge>;
    }
  };

  // Handle delete non-medication
  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteMedication({ id, type: 0 })).then((response) => {
          if (response.meta.requestStatus === 'fulfilled') {
            Swal.fire({
              title: 'Deleted!',
              text: 'Non-medication has been deleted successfully.',
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.payload || 'Failed to delete non-medication.',
              icon: 'error',
              confirmButtonText: 'Okay'
            });
          }
        });
      }
    });
  };

  // Get icon for non-medication type
  const getNonMedicationIcon = (medicationName) => {
    const found = nonMedicationTypes.find(type => 
      medicationName.toLowerCase().includes(type.label.toLowerCase())
    );
    return found ? found.icon : 'ri-capsule-line';
  };

  return (
    <>
      <LoaderModal showLoader={loading && page === 1} />
      <div id='content-page' className='content-page'>
        <Container>
          <Row>
            <Col sm="12">
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
                  <div className="header-title mb-2 mb-md-0">
                    <h4 className="card-title">
                      <i className="ri-first-aid-kit-line me-2"></i>
                      Administration of Non-Medication
                    </h4>
                    <p className="text-muted mb-0">
                      Lotion, Sunscreen, Insect repellent, Lip balm, Diaper rash cream
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted">Total: {meta.total} forms</span>
                    <Link 
                      to="/dashboard/app/non-medication/add"
                      className="btn btn-primary"
                    >
                      <i className="ri-add-line me-1"></i>
                      Add New Request
                    </Link>
                  </div>
                </Card.Header>
                <Card.Body>
                  {nonMedicationList?.length > 0 ? (
                    <>
                      <div className="table-responsive">
                        <table className="table table-striped table-hover">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">Non-Medication Details</th>
                              <th scope="col">Schedule</th>
                              <th scope="col">Duration</th>
                              <th scope="col">Status</th>
                              <th scope="col">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {nonMedicationList.map((nonMedication, index) => (
                              <tr key={nonMedication.id} className="align-middle">
                                <th scope="row">{(page - 1) * 10 + index + 1}</th>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="me-3">
                                      <i className={`${getNonMedicationIcon(nonMedication.medication_name)} text-primary fs-4`}></i>
                                    </div>
                                    <div>
                                      <h6 className="mb-1">{nonMedication.medication_name}</h6>
                                      <small className="text-muted">
                                        Parent: {nonMedication.parent_name}
                                      </small>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className="fw-semibold">
                                    {formatTime(nonMedication.times_to_be_administered)}
                                  </span>
                                </td>
                                <td>
                                  <div>
                                    <small className="d-block">
                                      <strong>Start:</strong> {formatDate(nonMedication.date_start)}
                                    </small>
                                    <small className="d-block">
                                      <strong>End:</strong> {formatDate(nonMedication.date_end)}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  {getStatusBadge(nonMedication.status)}
                                </td>
                                <td>
                                  {/* No btn-sm here — an icon-only .btn-sm is just the default
                                      button's tight rectangular padding wrapped around a single
                                      glyph, which reads as a tiny, hard-to-tap square in a table
                                      row. Default button size + a slightly larger glyph (fs-5)
                                      gives these a real tap target without changing the table's
                                      row height meaningfully. */}
                                  <div className="d-flex gap-1">
                                    <Link
                                      to={`/dashboard/app/non-medication/logs/${nonMedication.id}`}
                                      className="btn btn-outline-info"
                                      title="View Logs"
                                    >
                                      <i className="ri-file-list-2-line fs-5"></i>
                                    </Link>
                                    {(nonMedication.status === 1 || nonMedication.status === '1') && (
                                      <Link
                                        to={`/dashboard/app/non-medication/edit/${nonMedication.id}`}
                                        className="btn btn-outline-warning"
                                        title="Edit"
                                      >
                                        <i className="ri-edit-line fs-5"></i>
                                      </Link>
                                    )}
                                    {(nonMedication.status === 1 || nonMedication.status === '1') &&
                                     new Date(nonMedication.date_start) > new Date() && (
                                      <Button
                                        variant="outline-danger"
                                        onClick={() => handleDelete(nonMedication.id)}
                                        title="Delete"
                                      >
                                        <i className="ri-delete-bin-line fs-5"></i>
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Load More Button */}
                      {page < meta.last_page && (
                        <div className="text-center mt-3">
                          <Button 
                            variant="outline-primary" 
                            onClick={loadMore}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </span>
                                Loading...
                              </>
                            ) : (
                              <>
                                <i className="ri-arrow-down-line me-1"></i>
                                Load More
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <NoDataFound />
                  )}
                  
                  {/* Non-Medication Types Info */}
                  <Row className="mt-4">
                    <Col sm="12">
                      <Card className="bg-light border-0">
                        <Card.Body>
                          <h6 className="mb-3">
                            <i className="ri-information-line me-2"></i>
                            Approved Non-Medication Types
                          </h6>
                          <Row>
                            {nonMedicationTypes.map((type, index) => (
                              <Col md={2} sm={4} xs={6} key={index} className="mb-2">
                                <div className="text-center">
                                  <i className={`${type.icon} fs-3 text-primary mb-1`}></i>
                                  <small className="d-block">{type.label}</small>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  
                  {/* Summary Cards */}
                  {nonMedicationList?.length > 0 && (
                    <Row className="mt-4">
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="text-center">
                            <i className="ri-first-aid-kit-fill display-4 text-success mb-2"></i>
                            <h5 className="mb-1">
                              {nonMedicationList.filter(m => m.status === 1).length}
                            </h5>
                            <small className="text-muted">Active Requests</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="text-center">
                            <i className="ri-time-line display-4 text-primary mb-2"></i>
                            <h5 className="mb-1">
                              {nonMedicationList.filter(m => 
                                new Date(m.date_end) > new Date()
                              ).length}
                            </h5>
                            <small className="text-muted">Ongoing</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="text-center">
                            <i className="ri-check-line display-4 text-warning mb-2"></i>
                            <h5 className="mb-1">
                              {nonMedicationList.filter(m => 
                                new Date(m.date_end) < new Date()
                              ).length}
                            </h5>
                            <small className="text-muted">Completed</small>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  )
}

export default NonMedicationAdministrationList